import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Save, Settings } from "lucide-react";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useTaxProfile,
  useUpdateTaxProfile,
} from "@/lib/api/taxes.queries";
import type { TaxObligation, TaxPeriodicity } from "@/lib/api/taxes";
import { useActiveCompany } from "@/lib/company-context";

export const Route = createFileRoute("/companies/$companyId/impuestos/configuracion")({
  component: TaxConfigPage,
});

const PERIODICITY_LABEL: Record<TaxPeriodicity, string> = {
  monthly: "Mensual",
  bimonthly: "Bimestral",
  quarterly: "Trimestral",
  four_monthly: "Cuatrimestral",
  annual: "Anual",
  manual: "Manual",
};

type DraftObligation = Pick<
  TaxObligation,
  "taxKind" | "displayName" | "authority" | "formCode" | "paymentRequired"
> & {
  active: boolean;
  periodicity: TaxPeriodicity;
  rate: string;
  notes: string;
};

function TaxConfigPage() {
  return (
    <RequireAuth>
      <AppShell>
        <TaxConfigContent />
      </AppShell>
    </RequireAuth>
  );
}

function TaxConfigContent() {
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  const profileQuery = useTaxProfile(companyId);
  const updateProfile = useUpdateTaxProfile(companyId);
  const [draft, setDraft] = useState<DraftObligation[]>([]);

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (!profileQuery.data) return;
    setDraft(
      profileQuery.data.obligations.map((obligation) => ({
        taxKind: obligation.taxKind,
        displayName: obligation.displayName,
        authority: obligation.authority,
        formCode: obligation.formCode,
        paymentRequired: obligation.paymentRequired,
        active: obligation.active,
        periodicity: obligation.periodicity,
        rate: obligation.rate === null ? "" : String(obligation.rate),
        notes: obligation.notes ?? "",
      })),
    );
  }, [profileQuery.data]);

  const composition = profileQuery.data?.composition;
  const activeCount = useMemo(
    () => draft.filter((obligation) => obligation.active).length,
    [draft],
  );

  function updateDraft(
    taxKind: string,
    patch: Partial<Omit<DraftObligation, "taxKind">>,
  ) {
    setDraft((items) =>
      items.map((item) =>
        item.taxKind === taxKind ? { ...item, ...patch } : item,
      ),
    );
  }

  function save(e: React.FormEvent) {
    e.preventDefault();
    updateProfile.mutate(
      {
        obligations: draft.map((obligation) => ({
          taxKind: obligation.taxKind,
          active: obligation.active,
          periodicity: obligation.periodicity,
          rate: obligation.rate.trim() ? Number(obligation.rate) : null,
          notes: obligation.notes.trim() || null,
        })),
      },
      {
        onSuccess: () => toast.success("Configuracion tributaria guardada"),
        onError: (err) =>
          toast.error("No pudimos guardar la configuracion", {
            description: err.message,
          }),
      },
    );
  }

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 p-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1.5">
          <Link
            to="/companies/$companyId/impuestos"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Volver a impuestos
          </Link>
          <div className="flex items-center gap-2">
            <Settings className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-semibold tracking-tight">
              Configuracion tributaria
            </h1>
          </div>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Ajusta obligaciones, periodicidad y tarifas que no se pueden inferir
            completamente desde el RUT y los documentos.
          </p>
        </div>
        <Button type="submit" form="tax-config-form" disabled={updateProfile.isPending || !profileQuery.data?.editable}>
          <Save className="h-4 w-4" />
          Guardar
        </Button>
      </header>

      {profileQuery.isPending ? (
        <Skeleton className="h-96 rounded-lg" />
      ) : profileQuery.isError ? (
        <ErrorState
          title="No pudimos cargar la configuracion"
          description={profileQuery.error?.message ?? ""}
          onRetry={() => void profileQuery.refetch()}
          retryLabel="Reintentar"
        />
      ) : profileQuery.data ? (
        <>
          {!profileQuery.data.editable ? (
            <Alert variant="warning">
              <AlertTitle>Solo lectura</AlertTitle>
              <AlertDescription>
                Tu rol puede ver la configuracion, pero no editar obligaciones
                ni valores.
              </AlertDescription>
            </Alert>
          ) : null}

          <section className="grid gap-3 md:grid-cols-4">
            <Metric title="Obligaciones activas" value={String(activeCount)} />
            <Metric title="Regimen" value={composition?.regimeCode ?? "Sin dato"} />
            <Metric
              title="Responsabilidades"
              value={String(composition?.responsibilities.length ?? 0)}
            />
            <Metric
              title="Ciudad"
              value={composition?.cityCode ?? "Sin ciudad"}
            />
          </section>

          <form id="tax-config-form" className="grid gap-4" onSubmit={save}>
            {draft.map((obligation) => (
              <Card key={obligation.taxKind}>
                <CardHeader>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-base">
                        {obligation.displayName}
                      </CardTitle>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Badge variant="outline">{obligation.authority.toUpperCase()}</Badge>
                        {obligation.formCode ? (
                          <Badge variant="secondary">Formulario {obligation.formCode}</Badge>
                        ) : null}
                        {!obligation.paymentRequired ? (
                          <Badge variant="muted">Informativo</Badge>
                        ) : null}
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={obligation.active}
                        disabled={!profileQuery.data.editable}
                        onChange={(e) =>
                          updateDraft(obligation.taxKind, { active: e.target.checked })
                        }
                        className="h-4 w-4"
                      />
                      Activa
                    </label>
                  </div>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-[220px_180px_1fr]">
                  <div className="grid gap-1.5">
                    <Label>Periodicidad</Label>
                    <Select
                      value={obligation.periodicity}
                      disabled={!profileQuery.data.editable}
                      onValueChange={(value) =>
                        updateDraft(obligation.taxKind, {
                          periodicity: value as TaxPeriodicity,
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(PERIODICITY_LABEL).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor={`${obligation.taxKind}-rate`}>Tarifa %</Label>
                    <Input
                      id={`${obligation.taxKind}-rate`}
                      inputMode="decimal"
                      value={obligation.rate}
                      disabled={!profileQuery.data.editable}
                      onChange={(e) =>
                        updateDraft(obligation.taxKind, { rate: e.target.value })
                      }
                      placeholder="0"
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor={`${obligation.taxKind}-notes`}>Notas</Label>
                    <Input
                      id={`${obligation.taxKind}-notes`}
                      value={obligation.notes}
                      disabled={!profileQuery.data.editable}
                      onChange={(e) =>
                        updateDraft(obligation.taxKind, { notes: e.target.value })
                      }
                      placeholder="Criterio, ciudad, actividad o referencia"
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </form>
        </>
      ) : null}
    </div>
  );
}

function Metric(props: { title: string; value: string }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <p className="text-xs font-medium text-muted-foreground">{props.title}</p>
      <p className="mt-2 text-xl font-semibold">{props.value}</p>
    </div>
  );
}
